import React, { useEffect, useState, useCallback } from 'react';

const apiUrl = process.env.REACT_APP_API_URL;
const BASE_URL = `${apiUrl}/whatsapp`;

const overlayStyle = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.4)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1000
};

const modalStyle = {
  width: '900px',
  maxWidth: '95vw',
  maxHeight: '85vh',
  background: '#fff',
  borderRadius: 8,
  padding: 16,
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column'
};

const headerStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: 12
};

const contentStyle = {
  overflow: 'auto',
  border: '1px solid #e5e5e5',
  borderRadius: 6
};

const footerStyle = {
  display: 'flex',
  gap: 8,
  justifyContent: 'space-between',
  marginTop: 12
};

export default function CampaignContactsModal({
  show,
  onClose,
  token,
  campaignId,
  status, // 'sent' | 'delivered' | 'read' | 'failed'
  onBroadcastSelected
}) {
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [hasMore, setHasMore] = useState(false);
  const [lastKnownMaxPage, setLastKnownMaxPage] = useState(null); // we infer if has_more=false
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  const [selected, setSelected] = useState({}); // key: phoneWithCC → boolean

  const resetState = () => {
    setItems([]);
    setPage(1);
    setPageSize(20);
    setHasMore(false);
    setLastKnownMaxPage(null);
    setLoading(false);
    setErr(null);
    setSelected({});
  };

  const fetchPage = useCallback(async () => {
    if (!show || !campaignId || !status) return;
    try {
      setLoading(true);
      setErr(null);
      // Build URL with pagination
      const url = new URL(`${BASE_URL}/campaign_messages`);
      url.searchParams.set('campaign_id', campaignId);
      url.searchParams.set('status', status);
      url.searchParams.set('page', page);
      url.searchParams.set('page_size', pageSize);

      const res = await fetch(url.toString(), {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          // include both typical patterns just to be safe
          'Authorization': `Bearer ${token}`,
          'auth_token': token
        },
        credentials: 'include'
      });

      if (!res.ok) {
        throw new Error(`HTTP error! status: ${res.status}`);
      }

      const json = await res.json();

      if (!json?.success) {
        throw new Error(json?.message || 'Failed to fetch campaign messages');
      }

      const data = json.data || {};
      const list = data.items || [];
      const pagination = data.pagination || {};

      setItems(list);
      setHasMore(!!pagination.has_more);

      // If API doesn't return total pages, infer when has_more becomes false.
      if (pagination.has_more === false) {
        setLastKnownMaxPage(page);
      }

      // Maintain selection across pages where keys match
      // (no-op here; selection is per unique phone key)

    } catch (e) {
      console.error('Error fetching campaign messages:', e);
      setErr(e.message);
    } finally {
      setLoading(false);
    }
  }, [show, campaignId, status, page, pageSize, token]);

  useEffect(() => {
    if (!show) return;
    // When opening fresh, reset & fetch
    resetState();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, campaignId, status]);

  useEffect(() => {
    if (show && campaignId && status) {
      fetchPage();
    }
  }, [fetchPage, show, campaignId, status, page, pageSize]);

  const close = () => {
    onClose?.();
  };

  const toggleSelectAll = () => {
    const allSelected = items.every(item => isSelected(item));
    const next = { ...selected };
    if (allSelected) {
      // unselect all current page
      items.forEach(item => {
        const key = makeKey(item);
        delete next[key];
      });
    } else {
      // select all current page
      items.forEach(item => {
        const key = makeKey(item);
        next[key] = normalizeContact(item);
      });
    }
    setSelected(next);
  };

  const makeKey = (item) => {
    const cc = (item.country_code || '').toString().trim();
    const ph = (item.phone_number || '').toString().trim();
    return `${cc}${ph}`;
  };

  const isSelected = (item) => {
    const key = makeKey(item);
    return !!selected[key];
  };

  const handleRowToggle = (item) => {
    const key = makeKey(item);
    const next = { ...selected };
    if (next[key]) {
      delete next[key];
    } else {
      next[key] = normalizeContact(item);
    }
    setSelected(next);
  };

  // Convert API item into a contact usable by BroadcastModal
  // Keeping { name, phone } where phone is E.164-like: country_code + phone_number (no +)
  const normalizeContact = (item) => {
    const name = item.name || '';
    const cc = (item.country_code || '').toString().trim();
    const ph = (item.phone_number || '').toString().trim();
    const phone = cc && ph ? `${cc}${ph}` : ph || '';
    return { name, phone };
  };

  const broadcastSelected = () => {
    const list = Object.values(selected);
    onBroadcastSelected?.(list);
  };

  const pageDown = () => setPage(p => Math.max(1, p - 1));
  const pageUp = () => setPage(p => p + 1);

  const maxPageLabel = lastKnownMaxPage ? `${lastKnownMaxPage}` : hasMore ? '—' : `${page}`;
  const titleLabelMap = {
    sent: 'Sent',
    delivered: 'Delivered',
    read: 'Read',
    failed: 'FB Failed'
  };

  if (!show) return null;

  return (
    <div style={overlayStyle} onClick={close}>
      <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
        <div style={headerStyle}>
          <h3 style={{ margin: 0 }}>
            {titleLabelMap[status] || 'Contacts'} — Campaign: {campaignId}
          </h3>
          <button onClick={close} style={{ border: '1px solid #ddd', background: '#f8f8f8', padding: '6px 12px', borderRadius: 6 }}>
            Close
          </button>
        </div>

        <div style={{ marginBottom: 8, display: 'flex', gap: 12, alignItems: 'center' }}>
          <div>
            <strong>Page:</strong> {page} / {maxPageLabel} {lastKnownMaxPage ? '' : hasMore ? '(unknown max)' : ''}
          </div>
          <div>
            <label>
              Page Size:{' '}
              <input
                type="number"
                min={1}
                max={1000}
                value={pageSize}
                onChange={(e) => setPageSize(Math.max(1, Number(e.target.value) || 20))}
                style={{ width: 80 }}
              />
            </label>
          </div>
          <div>
            <button onClick={pageDown} disabled={page <= 1 || loading}>Previous</button>{' '}
            <button onClick={pageUp} disabled={!hasMore || loading}>Next</button>
          </div>
          <div style={{ marginLeft: 'auto' }}>
            <button onClick={toggleSelectAll}>
              {items.length > 0 && items.every(it => isSelected(it)) ? 'Unselect All (page)' : 'Select All (page)'}
            </button>
          </div>
        </div>

        <div style={contentStyle}>
          {loading ? (
            <div style={{ padding: 16 }}>Loading...</div>
          ) : err ? (
            <div style={{ color: 'red', padding: 16 }}>Error: {err}</div>
          ) : (
            <table width="100%" cellPadding="8" style={{ borderCollapse: 'collapse' }}>
              <thead style={{ position: 'sticky', top: 0, background: '#fafafa' }}>
                <tr>
                  <th align="left">Select</th>
                  <th align="left">Name</th>
                  <th align="left">Phone</th>
                  <th align="left">Country Code</th>
                  <th align="left">Status</th>
                  <th align="left">Latest Detail</th>
                  <th align="left">Status History</th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 ? (
                  <tr>
                    <td colSpan={7} style={{ padding: 16, textAlign: 'center' }}>No records found.</td>
                  </tr>
                ) : items.map((item, idx) => {
                  const key = makeKey(item);
                  const latestDetail = Array.isArray(item.status_history) && item.status_history.length > 0
                    ? item.status_history[0]?.details || item.status_history[0]?.status || ''
                    : '';
                  return (
                    <tr key={`${key}-${idx}`} style={{ borderTop: '1px solid #eee' }}>
                      <td>
                        <input
                          type="checkbox"
                          checked={!!selected[key]}
                          onChange={() => handleRowToggle(item)}
                        />
                      </td>
                      <td>{item.name || '-'}</td>
                      <td>{item.phone_number || '-'}</td>
                      <td>{item.country_code || '-'}</td>
                      <td>{item.status || '-'}</td>
                      <td>{latestDetail || '-'}</td>
                      <td>
                        {Array.isArray(item.status_history) && item.status_history.length > 0 ? (
                          <details>
                            <summary>View</summary>
                            <ul style={{ margin: '6px 0 0 16px', padding: 0 }}>
                              {item.status_history.map((s, i) => (
                                <li key={i} style={{ listStyle: 'disc' }}>
                                  <div><strong>{s.status}</strong> {s.details ? `- ${s.details}` : ''}</div>
                                  <div style={{ fontSize: 12, opacity: 0.8 }}>
                                    {s.timestamp || '-'} {s.error_code ? `(code: ${s.error_code})` : ''}
                                  </div>
                                </li>
                              ))}
                            </ul>
                          </details>
                        ) : '-'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <div style={footerStyle}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span><strong>Selected:</strong> {Object.keys(selected).length}</span>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={close}>Cancel</button>
            <button
              onClick={broadcastSelected}
              disabled={Object.keys(selected).length === 0}
              style={{ background: '#0b5ed7', color: '#fff', border: 'none', padding: '8px 12px', borderRadius: 6 }}
              title={Object.keys(selected).length === 0 ? 'Select at least one contact' : 'Broadcast to selected contacts'}
            >
              Broadcast Selected
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
